import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Briefcase, 
  Edit, 
  Save, 
  Plus, 
  Calendar, 
  Trash2 
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface WorkExperienceTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  onRefresh?: () => void;
}

const WorkExperienceTab: React.FC<WorkExperienceTabProps> = ({ 
  data, 
  loading, 
  isEditable, 
  editMode, 
  onEdit, 
  onSave, 
  onCancel,
  onRefresh 
}) => {
  const [editData, setEditData] = useState(() => {
    // Initialize with correct structure
    if (data) {
      if (Array.isArray(data)) {
        return { workExperiences: data, total_count: data.length };
      } else {
        return data;
      }
    }
    return {};
  });
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWorkExp, setNewWorkExp] = useState({
    company: '',
    title: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: ''
  });

  useEffect(() => {
    // Ensure editData has the correct structure for work experience
    if (data) {
      if (Array.isArray(data)) {
        setEditData({ workExperiences: data, total_count: data.length });
      } else {
        setEditData(data);
      }
    }
  }, [data]);

  if (loading) {
    return <Card><CardContent className="p-6">Loading work experience...</CardContent></Card>;
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    // Reset editData to match the original data structure
    if (data) {
      if (Array.isArray(data)) {
        setEditData({ workExperiences: data, total_count: data.length });
      } else {
        setEditData(data);
      }
    }
    onCancel();
  };

  const handleWorkExperienceChange = (index: number, field: string, value: any) => {
    console.log('🔧 handleWorkExperienceChange called:', { index, field, value });
    
    updateEditData((prev: any) => {
      console.log('🔧 Previous editData:', prev);
      
      // Get the current work experience array
      const currentWorkExp = prev?.workExperiences || workExperiences || [];
      console.log('🔧 Current work exp array:', currentWorkExp);
      
      // Update the specific work experience item
      const updatedWorkExp = currentWorkExp.map((exp: any, i: number) => 
        i === index ? { ...exp, [field]: value } : exp
      );
      
      const newData = {
        ...prev,
        workExperiences: updatedWorkExp
      };
      
      console.log('🔧 New editData:', newData);
      return newData;
    });
  };

  const handleAddNewWorkExp = async () => {
    if (!newWorkExp.company.trim()) {
      toast.error('Company is required');
      return;
    }
    
    if (!newWorkExp.title.trim()) {
      toast.error('Job title is required');
      return;
    }
    
    if (!newWorkExp.start_date.trim()) {
      toast.error('Start date is required');
      return;
    }

    try {
      const url = buildApiUrl('work-exp/me/work-experience');
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(newWorkExp)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to add work experience';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to add work experience.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Work experience added successfully');
        setNewWorkExp({
          company: '',
          title: '',
          location: '',
          start_date: '',
          end_date: '',
          is_current: false,
          description: ''
        });
        setShowAddForm(false);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to add work experience');
      }
    } catch (error: any) {
      console.error('❌ Error adding work experience:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error adding work experience: ${errorMessage}`);
    }
  };

  const handleDeleteWorkExp = async (workExpId: number, workExpTitle: string) => {
    console.log('🔍 handleDeleteWorkExp called with:', { workExpId, workExpTitle });
    
    if (!workExpId || workExpId === undefined || workExpId === null) {
      toast.error('Cannot delete work experience: Invalid work experience ID');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the work experience "${workExpTitle}"?`)) {
      return;
    }

    try {
      const url = buildApiUrl(`work-exp/me/work-experience/${workExpId}`);
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete work experience';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete work experience.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Work experience "${workExpTitle}" deleted successfully`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to delete work experience');
      }
    } catch (error: any) {
      console.error('❌ Error deleting work experience:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error deleting work experience: ${errorMessage}`);
    }
  };

  // Handle different possible data structures from API
  let workExperiences: any[] = [];
  let totalCount = 0;
  
  if (Array.isArray(data)) {
    // Data is directly an array of work experiences
    workExperiences = data;
    totalCount = data.length;
  } else if (data) {
    // Data is an object with nested work experience
    workExperiences = data.workExperiences || data.work_experience || data.data?.workExperiences || data.experiences || [];
    totalCount = data.total_count || data.totalCount || workExperiences.length;
  }

  // Debug logging to understand data structure
  console.log('🔍 WorkExperienceTab data:', data);
  console.log('🔍 WorkExperiences extracted:', workExperiences);
  console.log('🔍 Total count:', totalCount);
  console.log('🔍 Edit mode:', editMode);
  console.log('🔍 Edit data:', editData);
  console.log('🔍 Rendering array:', editMode ? (editData?.workExperiences || workExperiences) : workExperiences);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Work Experience ({totalCount})
        </CardTitle>
        <div className="flex gap-2">
          {isEditable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="bg-green-50 hover:bg-green-100 border-green-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Experience
            </Button>
          )}
          {isEditable && !editMode && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {workExperiences.length > 0 ? (
          <div className="space-y-4">
            {(editMode ? (editData?.workExperiences || workExperiences) : workExperiences)?.map((exp: any, index: number) => (
              <div key={exp.id || index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    {editMode ? (
                      <>
                        <input
                          type="text"
                          value={exp.jobTitle || exp.title || ''}
                          onChange={(e) => handleWorkExperienceChange(index, 'jobTitle', e.target.value)}
                          className="font-semibold text-lg border-b-2 border-gray-300 focus:border-blue-500 outline-none bg-transparent w-full"
                          placeholder="Job Title"
                        />
                        <input
                          type="text"
                          value={exp.companyName || exp.company || ''}
                          onChange={(e) => handleWorkExperienceChange(index, 'companyName', e.target.value)}
                          className="text-sm text-gray-600 mt-1 w-full border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                          placeholder="Company"
                        />
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold">{exp.jobTitle || exp.title}</h3>
                        <p className="text-sm text-gray-600">{exp.companyName || exp.company}</p>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {editMode ? (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={exp.isCurrentJob || exp.is_current || false}
                          onChange={(e) => handleWorkExperienceChange(index, 'isCurrentJob', e.target.checked)}
                        />
                        <span className="text-sm">Current</span>
                      </label>
                    ) : (
                      <Badge variant={exp.isCurrentJob || exp.is_current ? "default" : "secondary"}>
                        {exp.isCurrentJob || exp.is_current ? "Current" : "Previous"}
                      </Badge>
                    )}
                    {editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWorkExp(exp.id, exp.jobTitle || exp.title)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  {editMode ? (
                    <>
                      <input
                        type="date"
                        value={exp.startDate || exp.start_date || ''}
                        onChange={(e) => handleWorkExperienceChange(index, 'startDate', e.target.value)}
                        className="border rounded px-2 py-1"
                      />
                      <span>-</span>
                      {exp.isCurrentJob || exp.is_current ? (
                        <span>Present</span>
                      ) : (
                        <input
                          type="date"
                          value={exp.endDate || exp.end_date || ''}
                          onChange={(e) => handleWorkExperienceChange(index, 'endDate', e.target.value)}
                          className="border rounded px-2 py-1"
                        />
                      )}
                    </>
                  ) : (
                    <span>{exp.startDate || exp.start_date} - {exp.endDate || exp.end_date || 'Present'}</span>
                  )}
                </div>
                {editMode ? (
                  <textarea
                    value={exp.description || ''}
                    onChange={(e) => handleWorkExperienceChange(index, 'description', e.target.value)}
                    rows={3}
                    className="text-sm mt-2 w-full border border-gray-300 rounded-md px-3 py-2 focus:border-blue-500 outline-none"
                    placeholder="Job description"
                  />
                ) : (
                  exp.description && (
                    <p className="text-sm mt-2">{exp.description}</p>
                  )
                )}
                {editMode ? (
                  <input
                    type="text"
                    value={exp.technologies?.join(', ') || ''}
                    onChange={(e) => handleWorkExperienceChange(index, 'technologies', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                    className="text-sm mt-2 w-full border border-gray-300 rounded-md px-3 py-2 focus:border-blue-500 outline-none"
                    placeholder="Technologies (comma-separated)"
                  />
                ) : (
                  exp.technologies?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {exp.technologies.map((tech: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No work experience available</p>
        )}

        {showAddForm && (
          <div className="border rounded-lg p-4 mt-4 bg-gray-50">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Work Experience
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={newWorkExp.title}
                  onChange={(e) => setNewWorkExp(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company *
                </label>
                <input
                  type="text"
                  value={newWorkExp.company}
                  onChange={(e) => setNewWorkExp(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Tech Company Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={newWorkExp.location}
                  onChange={(e) => setNewWorkExp(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., San Francisco, CA"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={newWorkExp.is_current}
                    onChange={(e) => setNewWorkExp(prev => ({ ...prev, is_current: e.target.checked, end_date: e.target.checked ? '' : prev.end_date }))}
                  />
                  <span className="text-sm font-medium text-gray-700">Current Job</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={newWorkExp.start_date}
                  onChange={(e) => setNewWorkExp(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              {!newWorkExp.is_current && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newWorkExp.end_date}
                    onChange={(e) => setNewWorkExp(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Description
                </label>
                <textarea
                  value={newWorkExp.description}
                  onChange={(e) => setNewWorkExp(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of your role and responsibilities..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddNewWorkExp} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Add Work Experience
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkExperienceTab;
